import ContinueButton from "@components/ContinueButton";
import Error from "@hudson/ui/components/Error";
import Email from "@hudson/ui/components/FormFields/Email";
import Phone from "@hudson/ui/components/FormFields/Phone";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import { FormProvider, useForm } from "@hudson/ui/components/ReactHookForm";
import Success from "@hudson/ui/components/Success";
import Session from "@lib/session";
import useContent from "@lib/use_content";
import userProfileUpdate from "@services/user_profile_update";
import { useState } from "react";
import Name from "../../shared//Name";
import AltPhone from "../../shared/AltPhone";
import FaxNumber from "../../shared/FaxNumber";

interface FormProps {
  profile: {
    address?: string;
    altPhone?: string;
    faxNumber?: string;
    city?: string;
    email?: string;
    name?: string;
    phone?: string;
    state?: string;
    zip?: string;
  };
}

export default function Form({ profile }: FormProps) {
  const { data: cData, loading } = useContent([
    "beErrAltphoneFormat",
    "beErrPhoneFormat",
    "beErrFaxNumberFormat",
    "beErrEmailFormat",
    "beErrFieldRequired",
    "beErrProfileUpdate",
    "beProfileAltphoneLabel",
    "beProfileContactSectionTitle",
    "beProfileEmailLabel",
    "beProfileNameLabel",
    "beProfilePhoneLabel",
    "beFaxNumberLabel",
    "beProfileUpdateSubmitButton",
    "beProfileUpdateSuccessMsg",
    "beRequiredPlaceholder",
  ]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [success, setSuccess] = useState<boolean>();
  const [submitting, setSubmitting] = useState(false);

  const formMethods = useForm({
    defaultValues: {
      name: profile.name,
      address: profile.address,
      city: profile.city,
      state: profile.state,
      zipCode: profile.zip,
      emailAddress: profile.email,
      telephone: profile.phone,
      telephone2: profile.altPhone,
      faxNumber: profile.faxNumber,
    },
  });

  interface UpdateData {
    name?: String;
    address?: String;
    city?: String;
    state?: String;
    zipCode?: String;
    emailAddress?: String;
    telephone?: String;
    telephone2?: String;
    faxNumber?: String;
  }

  async function submitForm(data: UpdateData, showSaveMarkers: boolean) {
    const profileId = Session.getBooking().profiles.user;
    if (!profileId) return;

    setErrorMessage("");
    setSuccess(false);
    if (showSaveMarkers) setSubmitting(true);

    const success = await userProfileUpdate(profileId, { contact: data });

    if (!success) {
      if (showSaveMarkers) setErrorMessage(cData?.content.beErrProfileUpdate);
    } else if (success) {
      if (showSaveMarkers) setSuccess(true);
    }

    setSubmitting(false);
  }

  return loading ? (
    <ConcentricLoader className="max-w-2xl px-4 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <FormProvider {...formMethods}>
      <form
        id="update-user"
        onSubmit={(e) => {
          e.preventDefault();
          formMethods.handleSubmit((data) => submitForm(data, true))();
        }}
        noValidate
      >
        <div className="divide-y divide-gray-200">
          <div className="pb-8">
            <div className="mt-8 mb-6 space-y-1">
              <h3 className="text-xl font-bold leading-6 text-gray-900">
                {cData?.content.beProfileContactSectionTitle}
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4 ">
              <Name
                errorMessage={cData?.content.beErrFieldRequired}
                fieldName="name"
                label={cData.content.beProfileNameLabel}
                requiredPlaceholder={cData?.content.beRequiredPlaceholder}
                onBlur={formMethods.handleSubmit((data) => submitForm(data))}
              />

              <Email
                errorMessage={cData?.content.beErrFieldRequired}
                errorFormatMessage={cData?.content.beErrEmailFormat}
                fieldName="emailAddress"
                label={cData.content.beProfileEmailLabel}
                locked={true}
                widthClass="w-64"
              />

              {cData.content.beProfilePhoneLabel && (
                <Phone
                  errorFormatMessage={cData?.content.beErrPhoneFormat}
                  fieldName="telephone"
                  label={cData?.content.beProfilePhoneLabel}
                  onBlur={formMethods.handleSubmit((data) => submitForm(data))}
                />
              )}

              {cData.content.beProfileAltphoneLabel && (
                <AltPhone
                  errorFormatMessage={cData?.content.beErrAltphoneFormat}
                  fieldName="telephone2"
                  label={cData.content.beProfileAltphoneLabel}
                  onBlur={formMethods.handleSubmit((data) => submitForm(data))}
                />
              )}

              {cData.content.beFaxNumberLabel && (
                <FaxNumber
                  errorFormatMessage={cData?.content.beErrFaxNumberFormat || ""}
                  fieldName="faxNumber"
                  label={cData.content.beFaxNumberLabel}
                  onBlur={formMethods.handleSubmit((data) => submitForm(data))}
                />
              )}
            </div>
          </div>

          <div className="pt-8 mt-2">
            {errorMessage && <Error className="mb-6" message={errorMessage} heavyStyle scrollTo />}
            {success && (
              <Success
                className="mb-6"
                message={cData?.content.beProfileUpdateSuccessMsg}
                heavyStyle
                scrollTo
              />
            )}

            <ContinueButton
              alignLeft
              className="justify-self-start"
              disabled={submitting}
              label={cData?.content.beProfileUpdateSubmitButton}
              loading={submitting}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
